import React, { FunctionComponent, useEffect, useState } from 'react'
import { connect } from 'react-redux'

import { Callback } from '../../utils/types'
import { Product, ProductOption } from '../refiTransactions/types'
import StandaloneCheckBox from '../../components/standaloneCheckBox'
import LoaderButton from '../../components/loaderButton'
import ProductOptionsFilter from './coverages/productOptionsFilter'
import {
  getProductFilters,
  isMonthlyMembershipOption,
  UNLIMITED_FILTER_LABEL,
} from '../../helpers/refiAppHelper'
import ProductOptionCard from './coverages/productOptionCard'
import { updateSelectedOption, updateSurcharge } from './coverageActions'
import {
  updateProductPackage,
  applyMonthlyMembershipPosition,
  applyFloridaMarkupPosition,
} from './productPackageActions'
import { displayNotification } from '../../actions/notificationsActions'
import {
  selectCurrentRefiUser,
  selectCurrentProductPackageId,
  selectProductPackage,
  selectBusinessConfig,
} from '../../reducers'
import { User } from '../../core/types'
import { calculateSurcharges } from '../../helpers/calculateSurcharges'
import { calculateProductPricing } from '../../helpers/calculateProductPricing'
import { clearAutosaveMetadata } from '../../helpers/refiAppAccordionAutosave'

interface Props {
  products: Product[]
  onClickBack?: Callback
  onSubmit: Callback
  selectedCoverageProducts: {
    selectedOptions: { productId: number; id: number }[]
    selectedSurcharges: string[]
  }
  submitButtonText?: string
  isSubmitBtnLoading?: boolean
  updateSelectedOption: Callback
  updateProductPackage: Callback<Promise<any>>
  displayNotification: Callback
  updateSurcharge: Callback
  productPackageId: number
  user: User
  savedFilters?: {
    deductibles: string[]
    coverage_months: number[]
    coverage_miles: number[]
  }
  savedAddons?: string[]
  savedDiscount?: { amount: number; type: '%' | '#' }
  businessConfig: any
  selectedOption: any
  applyMonthlyMembershipPosition: Callback<Promise<any>>
  monthlyMembershipPosition: number
  monthlyMembershipDiscountable: boolean
  applyFloridaMarkupPosition: Callback<Promise<any>>
  floridaMarkupPosition: number
  floridaMarkupDiscountable: boolean
  selectionSaved: boolean
}

const initialFilters = {
  deductibles: [] as string[],
  coverage_months: [] as number[],
  coverage_miles: [] as number[],
}

export const ProductsForm: FunctionComponent<Props> = ({
  onSubmit,
  products,
  onClickBack,
  selectedCoverageProducts,
  submitButtonText = 'Next',
  isSubmitBtnLoading = false,
  updateSelectedOption,
  displayNotification,
  savedFilters,
  savedAddons,
  savedDiscount,
  updateSurcharge,
  businessConfig,
  selectedOption,
  productPackageId,
  applyMonthlyMembershipPosition,
  monthlyMembershipPosition,
  monthlyMembershipDiscountable,
  applyFloridaMarkupPosition,
  floridaMarkupPosition,
  floridaMarkupDiscountable,
  selectionSaved,
  user,
}) => {
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [isDeclineCoverage, setIsDeclineCoverage] = useState(false)
  const [filters, setFilters] = useState({})
  const [selectedFilters, setSelectedFilters] = useState(initialFilters)
  const [filteredOptions, setFilteredOptions] = useState<any>([])
  const [discount, setDiscount] = useState<{ amount: number; type: '%' | '#' }>(
    { amount: 0, type: '%' }
  )
  const [selectedProductAndOption, setSelectedProductAndOption] = useState({
    productId: null,
    optionId: null,
  })

  // A monthly membership is discounted by stepping the ladder below, not by the
  // free-form editor, which is why that editor is disabled once one is selected.
  // The position count comes off the selected option, not the package: it is a
  // property of the plan's configuration, so the controls can appear as soon as
  // an option is picked instead of waiting for the selection to be persisted.
  const monthlyMembershipPositionCount =
    (selectedCoverageProducts?.selectedOptions || []).reduce((count, selected) => {
      const option = products
        .flatMap((p) => p.options || [])
        .find((o) => String(o.id) === String(selected.id))

      return Math.max(count, option?.monthly_membership_position_count || 0)
    }, 0)

  const monthlyMembershipSelected = (
    selectedCoverageProducts?.selectedOptions || []
  ).some((selected) =>
    products.some((p) =>
      p.options?.some(
        (o) =>
          String(o.id) === String(selected.id) && isMonthlyMembershipOption(o)
      )
    )
  )

  // Florida files its rates, so the free-form discount is disabled there and the
  // agent steps through fixed configured markup tiers instead.
  const discountDisabled =
    user.currentAddress?.state === 'FL' || monthlyMembershipSelected

  const floridaMarkupPositionCount = (
    selectedCoverageProducts?.selectedOptions || []
  ).reduce((count, selected) => {
    const option = products
      .flatMap((p) => p.options || [])
      .find((o) => String(o.id) === String(selected.id))

    return Math.max(count, option?.florida_markup_position_count || 0)
  }, 0)

  useEffect(() => {
    if (savedFilters && Array.isArray(savedFilters.deductibles)) {
      setSelectedFilters(savedFilters)
    }
  }, [savedFilters])

  useEffect(() => {
    if (savedDiscount && typeof savedDiscount.amount === 'number') {
      setDiscount(savedDiscount)
    }
  }, [savedDiscount])

  // A monthly membership is priced by its configured margin tier. The discount
  // editor is hidden for it, but an amount entered against a fixed-term plan the
  // agent had selected first is still in state, so drop it on the way into
  // pricing and out to the API rather than relying on the editor being hidden.
  // The server clears it too -- see ProductPackages::Update.
  const effectiveDiscount = monthlyMembershipSelected ? undefined : discount

  useEffect(() => {
    if (Array.isArray(savedAddons)) {
      for (let code of savedAddons) {
        // prevent toggling it off if already added
        if (
          !selectedCoverageProducts.selectedSurcharges.includes(code as never)
        ) {
          updateSurcharge(code)
        }
      }
    }
  }, [savedAddons, updateSurcharge])

  // seed selected product on load / Continue Application
  useEffect(() => {
    setSelectedProductAndOption({
      productId: selectedOption.productId,
      optionId: selectedOption.id,
    })
    updateSelectedOption(selectedOption)
  }, [selectedOption.productId])

  const profitConfig = businessConfig.profit || {}
  const { profit_discount_percent, profit_discount_max } = profitConfig

  const handleSubmit = (e) => {
    e.preventDefault()
    const form = e.currentTarget as HTMLFormElement
    const isAutosaveMode = form.getAttribute('data-autosave') === 'true'
    clearAutosaveMetadata(form)
    setIsFormDirty(false)
    onSubmit({
      isDeclineCoverage,
      filters: selectedFilters,
      addons: selectedCoverageProducts.selectedSurcharges,
      options: selectedCoverageProducts.selectedOptions.map((o: any) => [
        o.productId,
        o.id,
      ]),
      discount: effectiveDiscount,
      autosaveMode: isAutosaveMode,
    })
  }

  const onChangeFilters = (filter) => {
    setIsFormDirty(true)
    const newFilters = { ...selectedFilters, ...filter }
    setSelectedFilters(newFilters)
    setSelectedProductAndOption({ productId: null, optionId: null })
    updateSelectedOption()
    setIsDeclineCoverage(false)
  }

  const onSelectProduct = (option) => {
    setIsFormDirty(true)
    setIsDeclineCoverage(false)
    setSelectedProductAndOption({
      productId: option.productId,
      optionId: option.id,
    })
    updateSelectedOption(option)
  }

  const onDeclineCoverage = () => {
    setIsFormDirty(true)
    setIsDeclineCoverage((current) => !current)
    updateSelectedOption()
  }

  const selectedSurchargesInOptions = (o: ProductOption) => {
    //does the available surcharges from a product option inclues one of the selected surcharges?
    let hasAddon = false
    let simpleSur = o.surcharges.map((s) => s.SurchargeCode)
    let sur = [''] //this fixes the type problem in sur.includes(s)
      .concat(selectedCoverageProducts.selectedSurcharges)
      .slice(1)
    hasAddon = simpleSur.some((s) => {
      if (sur.length === 0) return true
      return sur.includes(s)
    })

    return hasAddon
  }

  // The synthetic "Unlimited - Renews Monthly" entry matches any monthly
  // membership option rather than an exact numeric value, because an M2M
  // option's stored months (1) and miles (999999) are not what is displayed.
  const matchesCoverageFilter = (selected: any[], value: any, option: any) => {
    if (selected.length === 0) return true
    if (isMonthlyMembershipOption(option)) {
      return selected.includes(UNLIMITED_FILTER_LABEL)
    }

    return selected.includes(value)
  }

  const getFilteredOptions = () => {
    const filteredOptions = products.reduce<any>((acc, p) => {
      p.options.forEach((o) => {
        // Monthly membership plans carry their own add-ons (often none of the
        // ones an organization default-selects), so a default add-on selection
        // would silently hide every M2M plan. Exempt them, the same way the
        // coverage matrix is bypassed for them.
        let hasAddon =
          isMonthlyMembershipOption(o) || selectedSurchargesInOptions(o)
        if (
          (selectedFilters.deductibles.length === 0 ||
            selectedFilters.deductibles.includes(o.deductible)) &&
          matchesCoverageFilter(
            selectedFilters.coverage_miles,
            o.coverage_miles,
            o
          ) &&
          matchesCoverageFilter(
            selectedFilters.coverage_months,
            o.coverage_months,
            o
          ) &&
          hasAddon
        ) {
          acc.push({
            // Because GraphQL returns numbers as strings, we need to convert them
            productId: String(p.id),
            productDescription: p.description,
            productIsNew: p.is_new,
            ...o,
          })
        }
      })
      return acc
    }, [])

    // Monthly membership plans show first; everything else keeps the order the
    // provider returned it in.
    const sortedOptions = [
      ...filteredOptions.filter(isMonthlyMembershipOption),
      ...filteredOptions.filter((o) => !isMonthlyMembershipOption(o)),
    ]

    setFilteredOptions(sortedOptions)
    return sortedOptions
  }

  // Each click steps to the next configured margin position, which is a lower
  // markup and so a lower monthly price. Reset returns to the initial price.
  const onDiscount = () => {
    applyMonthlyMembershipPosition(
      productPackageId,
      monthlyMembershipPosition + 1
    ).then(() => getFilteredOptions())
  }

  const onResetDiscount = () => {
    applyMonthlyMembershipPosition(productPackageId, 0).then(() =>
      getFilteredOptions()
    )
  }

  const onFloridaDiscount = () => {
    applyFloridaMarkupPosition(productPackageId, floridaMarkupPosition + 1).then(
      () => getFilteredOptions()
    )
  }

  const onFloridaResetDiscount = () => {
    applyFloridaMarkupPosition(productPackageId, 0).then(() =>
      getFilteredOptions()
    )
  }

  // A package is one or the other: monthly membership prices from the plan's
  // own margins, Florida from the organization's markup tiers. Both step
  // through fixed positions, so they share one control.
  // Stepping a position reprices the saved option, so neither ladder can do
  // anything until the agent's selection has been persisted -- before that the
  // controls rendered inert, which read as broken rather than as "not yet".
  const monthlyMembershipLadder =
    selectionSaved && monthlyMembershipSelected && monthlyMembershipPositionCount > 1
      ? {
          position: monthlyMembershipPosition,
          count: monthlyMembershipPositionCount,
          discountable: monthlyMembershipDiscountable,
          onStep: onDiscount,
          onReset: onResetDiscount,
          discountTestId: 'm2m-discount-button',
          resetTestId: 'm2m-reset-discount-button',
        }
      : null

  const floridaLadder =
    selectionSaved && floridaMarkupPositionCount > 1
      ? {
          position: floridaMarkupPosition,
          count: floridaMarkupPositionCount,
          discountable: floridaMarkupDiscountable,
          onStep: onFloridaDiscount,
          onReset: onFloridaResetDiscount,
          discountTestId: 'florida-discount-button',
          resetTestId: 'florida-reset-discount-button',
        }
      : null

  const discountLadder = monthlyMembershipLadder || floridaLadder

  const filtersEmpty = () => {
    const { deductibles, coverage_miles, coverage_months } = selectedFilters

    return (
      !deductibles.length && !coverage_miles.length && !coverage_months.length
    )
  }

  const onChangeSurcharges = () => {
    setIsFormDirty(true)
    setIsDeclineCoverage(false)
    getFilteredOptions()
  }

  const onApplyDiscount = ({ amount, discountType }) => {
    setIsFormDirty(true)
    // Handle clearing discount when amount is empty
    if (!amount || amount.trim() === '') {
      setDiscount({ amount: 0, type: discountType })
      return null // No correction needed
    }

    const numRaw = parseFloat(amount)
    if (!isNaN(numRaw)) {
      let num = numRaw
      let correctedAmount: number | null = null // Track if we made a correction

      if (discountType === '%') {
        // First check basic percentage limit
        if (
          typeof profit_discount_percent === 'number' &&
          num > profit_discount_percent
        ) {
          num = profit_discount_percent
          correctedAmount = num
          displayNotification(
            `Max discount is ${profit_discount_percent}%`,
            'warning'
          )
        }

        // Smart percentage validation
        if (
          typeof profit_discount_max === 'number' &&
          selectedProductAndOption.optionId
        ) {
          const selectedOptionDetails = filteredOptions.find(
            (opt) => opt.id === selectedProductAndOption.optionId
          )

          if (selectedOptionDetails) {
            const surchargesValues = calculateSurcharges(
              selectedOptionDetails.surcharges,
              selectedCoverageProducts.selectedSurcharges,
              'Price',
              'MonthlyPayment'
            )

            const { totalCost: currentTotalPrice } = calculateProductPricing({
              total_cost: selectedOptionDetails.total_cost,
              monthly_payment: selectedOptionDetails.monthly_payment,
              surchargesValues,
              discount: null,
              coverage_months: selectedOptionDetails.coverage_months,
              monthly_membership: isMonthlyMembershipOption(
                selectedOptionDetails
              ),
            })

            const calculatedDiscountAmount = (currentTotalPrice * num) / 100

            if (calculatedDiscountAmount > profit_discount_max) {
              const maxAllowedPercentage =
                (profit_discount_max / currentTotalPrice) * 100
              num = Math.floor(maxAllowedPercentage * 100) / 100
              correctedAmount = num

              displayNotification(
                `Discount amount cannot exceed $${profit_discount_max.toFixed(
                  2
                )}. Maximum percentage for this price is ${num}%`,
                'warning'
              )
            }
          }
        }
      } else {
        // Fixed dollar amount validation
        if (
          typeof profit_discount_max === 'number' &&
          num > profit_discount_max
        ) {
          num = profit_discount_max
          correctedAmount = num
          displayNotification(
            `Max discount is $${profit_discount_max.toFixed(2)}`,
            'warning'
          )
        }
      }

      let finalAmount = typeof num === 'undefined' ? discount?.amount : num
      let finalDiscType =
        typeof discountType === 'undefined' ? discount?.type : discountType
      setDiscount({ amount: finalAmount, type: finalDiscType })

      // Return corrected amount if we made a correction
      return correctedAmount !== null
        ? { correctedAmount, correctedType: discountType }
        : null
    }

    return null
  }

  const renderProducts = () => {
    if (filteredOptions.length === 0) {
      return (
        <div className="products-filter-not-found-label-container">
          <span>No products found, try selecting different filters</span>
        </div>
      )
    }

    return filteredOptions.map((option) => {
      return (
        <ProductOptionCard
          key={option.id}
          data={option}
          onSelect={onSelectProduct}
          selected={selectedProductAndOption.optionId === option.id}
          selectedSurcharges={selectedCoverageProducts.selectedSurcharges}
          discount={effectiveDiscount}
        />
      )
    })
  }

  useEffect(() => {
    setFilters(getProductFilters(products))
  }, [])

  useEffect(() => {
    // If no product is selected and there are no filters set yet,
    // then assume that this is the first run and return until we have filters.
    // This works for now because we're expecting default filters to be set.
    // The issue this is trying to solve is auto-selecting the lowest option before they're set
    const saved = selectedCoverageProducts.selectedOptions[0]
    if (!saved && filtersEmpty()) {
      return
    }

    // get opts straight from function so we don't have to wait for the state to update
    let filteredOpts = getFilteredOptions()

    // auto-select saved option if present, otherwise pick the lowest deductible
    // but only if nothing is currently selected locally (to avoid re-trigger loops)
    if (filteredOpts.length > 0 && selectedProductAndOption.optionId == null) {
      if (saved) {
        const match = filteredOpts.find(
          (opt) => opt.productId === saved.productId && opt.id === saved.id
        )
        if (match) {
          onSelectProduct(match)
          return
        }
      }
      const lowestOption = filteredOpts.reduce(
        (min, option) => (option.deductible < min.deductible ? option : min),
        filteredOpts[0]
      )
      onSelectProduct(lowestOption)
    }
  }, [
    selectedFilters,
    selectedCoverageProducts.selectedOptions,
    selectedCoverageProducts.selectedSurcharges,
    selectedProductAndOption.optionId,
    products,
  ])

  return (
    <form
      className="form"
      name="ProductsForm"
      data-dirty={isFormDirty ? 'true' : 'false'}
      onSubmit={handleSubmit}
    >
      <ProductOptionsFilter
        data={filters}
        key={'deductibles-filter'}
        onChangeFilters={onChangeFilters}
        onChangeSurcharges={onChangeSurcharges}
        onApplyDiscount={onApplyDiscount}
        savedFilters={savedFilters}
        savedAddons={savedAddons}
        savedDiscount={savedDiscount}
        discountDisabled={discountDisabled}
      />
      <br />
      <div className="coverage__header">
        {isDeclineCoverage
          ? 'Coverage/protection declined'
          : 'Compare & select your coverage/protection'}
      </div>
      <div className="product-selection-card-container">
        {!isDeclineCoverage && renderProducts()}
      </div>
      <div>
        <div className="refi-app__section-header">
          <div style={{ display: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div className="coverage__sub-header" onClick={onDeclineCoverage}>
                No Thanks
              </div>
              <StandaloneCheckBox
                iconSize="20px"
                name="decline-coverage"
                uid="decline-coverage"
                checked={isDeclineCoverage}
                onChange={onDeclineCoverage}
                data-testid="decline-tickbox"
              />
            </div>
          </div>
          <br />
          <hr className="form__hr" />
        </div>
      </div>

      {onClickBack && (
        <LoaderButton
          type="button"
          className="button button-drive-america button--inline"
          onClick={onClickBack}
        >
          Back
        </LoaderButton>
      )}
      <LoaderButton
        type="submit"
        className="button button-drive-america"
        disabled={!isDeclineCoverage && !selectedProductAndOption.optionId}
        isLoading={isSubmitBtnLoading}
        data-testid={'review-agreement-button'}
      >
        {submitButtonText}
      </LoaderButton>

      {discountLadder && (
        <>
          <LoaderButton
            type="button"
            className="button button-drive-america button--inline"
            onClick={discountLadder.onStep}
            disabled={!discountLadder.discountable}
            data-testid={discountLadder.discountTestId}
          >
            Discount
          </LoaderButton>
          <LoaderButton
            type="button"
            className="button button-drive-america button--inline"
            onClick={discountLadder.onReset}
            disabled={discountLadder.position === 0}
            data-testid={discountLadder.resetTestId}
          >
            Reset
          </LoaderButton>
          <span className="coverage__sub-header">
            {discountLadder.position === 0
              ? 'Initial price'
              : `Discount ${discountLadder.position} of ${
                  discountLadder.count - 1
                }`}
          </span>
        </>
      )}
    </form>
  )
}

export const mapStateToProps = (state: any) => {
  const productPackageId = selectCurrentProductPackageId(state)
  const pkg = selectProductPackage(state.productPackages, productPackageId)
  const businessConfig = selectBusinessConfig(state)

  // The filters and addons should default to the configuration's fields.
  // Once a product has been selected, then they should load from the package's fields
  let savedFilters = businessConfig.packages?.filters
  let savedAddons = businessConfig.packages?.addons
  if (pkg.status === 'selected') {
    savedFilters = pkg.filters
    savedAddons = pkg.addons
  } else {
    // No product has been selected yet, and the addons will default to the configuration's field.
    // This field might include addons that aren't available in the current package's products.
    // Also on this note, the same addon might have different surcharge codes for different vehicles.
    // Ex: "Extended Maintenance - Annual" is code '911' for Ford and '913' for BMW.
    // So the provide "Extended Maintenance - Annual" as a default addon, both codes must be present.
    // To handle this, filter the default addons by those available for the package.
    // TODO: a better place for this would be "useEffect" so that it doesn't disrupt rendering
    savedAddons = savedAddons?.filter((code) => {
      for (let product of pkg?.products || []) {
        for (let option of product.options) {
          for (let surcharge of option.surcharges) {
            if (surcharge.SurchargeCode === code) {
              return true
            }
          }
        }
      }
      return false
    })
  }

  // seed selected product on load / Continue Application
  const selectedProduct =
    pkg.products.find((p) => !!p.selectedProductOptionId) || {}

  const selectedOption =
    selectedProduct?.options?.find(
      (o) => o.id === selectedProduct.selectedProductOptionId
    ) || {}

  return {
    productPackageId,
    user: selectCurrentRefiUser(state),
    savedFilters,
    savedAddons,
    savedDiscount: pkg.discount,
    businessConfig,
    selectedOption: {
      ...selectedOption,
      productId: selectedProduct.id,
      productDescription: selectedProduct.description,
    },
    monthlyMembershipPosition: pkg.monthlyMembershipPosition || 0,
    monthlyMembershipDiscountable: !!pkg.monthlyMembershipDiscountable,
    floridaMarkupPosition: pkg.floridaMarkupPosition || 0,
    floridaMarkupDiscountable: !!pkg.floridaMarkupDiscountable,
    // `selectedProduct` above is found by selectedProductOptionId, which only
    // exists once the selection has been saved.
    selectionSaved: !!selectedProduct.selectedProductOptionId,
  }
}

export default connect(mapStateToProps, {
  applyMonthlyMembershipPosition,
  applyFloridaMarkupPosition,
  updateSelectedOption,
  updateProductPackage,
  displayNotification,
  updateSurcharge,
})(ProductsForm)
